import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import MobileNav from "@/components/MobileNav";
import BetSlipCard from "@/components/BetSlipCard";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, PlusCircle, Trophy, Flame, Timer } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrency } from "@/lib/format";

export default function DashboardPage() {
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const nav = useNavigate();
  const { settings } = useSettings();

  const load = async () => {
    try {
      const { data } = await api.get("/slips");
      setSlips(data);
    } catch { toast.error("Failed to load slips"); }
    finally { setLoading(false); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/slips/refresh-all");
      setSlips(data);
      toast.success("Live scores updated");
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => { if (slips.some(s => ["pending", "winning", "losing"].includes(s.status))) refresh(); }, 45000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slips.length]);

  const active = slips.filter(s => ["pending", "winning", "losing"].includes(s.status));
  const settled = slips.filter(s => ["won", "lost", "settled"].includes(s.status));
  const wins = slips.filter(s => s.status === "won").length;
  const winnings = settled.reduce((a, s) => a + (s.status === "won" ? (s.projected_payout - s.stake) : -s.stake), 0);

  return (
    <div className="min-h-screen bg-[#0A0D14] pb-24 md:pb-8">
      <AppHeader />
      <MobileNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-400 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Live Dashboard
            </div>
            <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Your Slips
            </h1>
          </div>
          <div className="flex gap-2">
            <Button data-testid="refresh-button" variant="outline" onClick={refresh} disabled={refreshing} className="border-white/10 text-slate-200 bg-white/5 hover:bg-white/10">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button data-testid="add-slip-button" onClick={() => nav("/add")} className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider">
              <PlusCircle className="h-4 w-4 mr-2" /> New Slip
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={<Flame className="h-4 w-4" />} label="Active" value={active.length} accent="emerald" />
          <StatCard icon={<Trophy className="h-4 w-4" />} label="Won" value={wins} accent="emerald" />
          <StatCard icon={<Timer className="h-4 w-4" />} label="Settled" value={settled.length} accent="slate" />
          <StatCard icon={<Trophy className="h-4 w-4" />} label="Net" value={`${winnings >= 0 ? "+" : "-"}${formatCurrency(Math.abs(winnings), settings.currency)}`} accent={winnings >= 0 ? "emerald" : "red"} />
        </div>

        {loading ? (
          <div className="text-slate-400 text-center py-16">Loading...</div>
        ) : slips.length === 0 ? (
          <EmptyState onAdd={() => nav("/add")} />
        ) : (
          <>
            <SectionHeader>Active</SectionHeader>
            {active.length === 0 ? (
              <p className="text-slate-500 text-sm mb-6">No live slips right now.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 mb-8">
                {active.map(s => <BetSlipCard key={s.slip_id} slip={s} onOpen={() => nav(`/slip/${s.slip_id}`)} onMuteChange={(id, muted) => setSlips(xs => xs.map(x => x.slip_id === id ? { ...x, notifications_muted: muted } : x))} />)}
              </div>
            )}
            {settled.length > 0 && (
              <>
                <SectionHeader>Settled</SectionHeader>
                <div className="grid gap-4 md:grid-cols-2">
                  {settled.map(s => <BetSlipCard key={s.slip_id} slip={s} onOpen={() => nav(`/slip/${s.slip_id}`)} onMuteChange={(id, muted) => setSlips(xs => xs.map(x => x.slip_id === id ? { ...x, notifications_muted: muted } : x))} />)}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-300" : accent === "red" ? "text-red-300" : "text-slate-300";
  return (
    <div className="rounded-lg bg-[#121824] border border-white/10 p-3">
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-widest ${color}`}>{icon} {label}</div>
      <div className="text-xl sm:text-2xl font-mono font-bold text-white mt-1">{value}</div>
    </div>
  );
}
function SectionHeader({ children }) {
  return <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">{children}</h2>;
}
function EmptyState({ onAdd }) {
  return (
    <div className="border border-dashed border-white/10 rounded-xl p-10 text-center bg-[#121824]/60">
      <div className="text-lg font-bold uppercase tracking-wide text-white mb-2" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Track your first slip</div>
      <p className="text-slate-400 text-sm mb-4">Upload a screenshot of your NFL bet or enter the details manually.</p>
      <Button data-testid="empty-add-slip-button" onClick={onAdd} className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider">
        <PlusCircle className="h-4 w-4 mr-2" /> Add Slip
      </Button>
    </div>
  );
}
